import { useMemo, useState } from 'react';
import deepEqual from 'react-fast-compare';

import { includesObject } from '#/utils/functional/array/includesObject';

export function useTransitionGroup<T>(data: T[], duration: number) {
  type GroupState = {
    item: T;
    stage: string;
  };

  type ItemWithIndex = {
    item: T;
    index: number;
  };

  const initialGroup: GroupState[] = data.map((item) => ({
    item,
    stage: 'enter',
  }));

  const [groupState, setListState] = useState<GroupState[]>(initialGroup);

  useMemo(() => {
    const newItemsWithIndex: ItemWithIndex[] = [];

    data.forEach((d, index) => {
      if (groupState.every(({ item }) => !deepEqual(item, d))) {
        newItemsWithIndex.push({ item: d, index });
      }
    });

    // subtract item to be removed from chart
    // item goes to `exit` stage first
    const subtractItemStates: GroupState[] = groupState.filter(
      ({ item, stage }) => !includesObject(data, item) && stage !== 'exit',
    );

    const subtractItems: T[] = subtractItemStates.map((si) => si.item);

    if (newItemsWithIndex.length > 0 && subtractItemStates.length > 0) {
      const handleExitTransition = (prev: GroupState[]) =>
        prev.map(({ item, stage }) =>
          includesObject(subtractItems, item)
            ? { item, stage: 'exit' }
            : { item, stage },
        );

      setListState(handleExitTransition);

      setTimeout(() => {
        const handleRemoveItems = (prev: GroupState[]) =>
          prev.filter(({ item }) => !includesObject(subtractItems, item));

        setListState(handleRemoveItems);
      }, duration);
    }

    // item added in `from` stage
    // once subtracted items have been unmounted
    if (newItemsWithIndex.length > 0 && groupState.length < data.length) {
      const handleFromTransition = (prev: GroupState[]) =>
        newItemsWithIndex.reduce(
          (acc: GroupState[], { item, index }: ItemWithIndex) => [
            ...acc.slice(0, index),
            {
              item,
              stage: 'from',
            },
            ...acc.slice(index),
          ],
          prev,
        );

      setListState(handleFromTransition);
    }

    // item goes to `enter` stage
    if (
      newItemsWithIndex.length === 0 &&
      groupState.some(({ stage }) => stage === 'from')
    ) {
      setTimeout(() => {
        const handleEnterTransition = (prev: GroupState[]) =>
          prev.map(({ item, stage }) => ({
            item,
            stage: stage === 'from' ? 'enter' : stage,
          }));

        setListState(handleEnterTransition);
      });
    }
  }, [duration, groupState, data]);

  function transitionFn<R>(
    renderCallback: (item: T, stage: string, index: number) => R,
  ) {
    return groupState.map(({ item, stage }, index) =>
      renderCallback(item, stage, index),
    );
  }

  return transitionFn;
}
